package se.mittpass.web.pages

import se.mittpass.web.*
import se.mittpass.web.components.*
import se.mittpass.web.pages.*
import se.mittpass.web.viewmodel.*
import se.mittpass.web.style.*


import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.attributes.*
import se.mittpass.shared.api.*
import se.mittpass.shared.repository.ClaimsRepository
import se.mittpass.shared.repository.SlotsRepository
import kotlin.math.*

private val staffScope = MainScope()

@Composable
fun StaffDashboardPage(workplaceId: Long, userInfo: UserInfo?, onNavigate: (String) -> Unit) {
    var stats by remember { mutableStateOf<StaffStatsDto?>(null) }
    var activeAttendance by remember { mutableStateOf<AttendanceDto?>(null) }
    var myClaims by remember { mutableStateOf<List<MyClaimDto>>(emptyList()) }
    var availableSlots by remember { mutableStateOf<List<SlotDto>>(emptyList()) }
    var haccpExpected by remember { mutableStateOf(0) }
    var haccpCompleted by remember { mutableStateOf(0) }
    var recentMessages by remember { mutableStateOf<List<MessageDto>>(emptyList()) }
    
    var geoSettings by remember { mutableStateOf<WorkplaceGeoDto?>(null) }
    var currentDistance by remember { mutableStateOf<Double?>(null) }
    var geoError by remember { mutableStateOf<String?>(null) }

    var loading by remember { mutableStateOf(true) }
    var checkingIn by remember { mutableStateOf(false) }
    var checkingOut by remember { mutableStateOf(false) }
    var exitConfirmClaim by remember { mutableStateOf<MyClaimDto?>(null) }
    var claimConfirmSlot by remember { mutableStateOf<SlotDto?>(null) }
    var feedbackMsg by remember { mutableStateOf<String?>(null) }

    val claimsRepo = remember { ClaimsRepository(httpClient) }
    val slotsRepo = remember { SlotsRepository(httpClient) }

    fun refresh() {
        staffScope.launch {
            staffRepo.getMyStats(workplaceId).onSuccess { stats = it }
            attendanceRepo.getMyActiveAttendance(workplaceId).onSuccess { activeAttendance = it }
            claimsRepo.getMyClaims().onSuccess { myClaims = it.filter { c -> c.workplaceId == workplaceId } }
            
            // Get upcoming available slots for next 14 days
            val today = todayLocalDate()
            val future = addDays(today, 14)
            slotsRepo.getSlotsForRange(workplaceId, today, future).onSuccess { list ->
                availableSlots = list.filter { s ->
                    s.assignedToUserId == null && s.needCoverage
                }
            }

            // Get HACCP controls
            foodSafetyRepo.getToday(workplaceId).onSuccess { items ->
                haccpExpected = items.size
                haccpCompleted = items.count { it.completedToday }
            }

            // Get messages
            messageRepo.getMessages(workplaceId).onSuccess { msgs ->
                recentMessages = msgs.sortedByDescending { it.createdAt }.take(2)
            }

            // Get Geolocation settings
            geoRepo.getGeo(workplaceId).onSuccess { geoSettings = it }
            
            loading = false
        }
    }

    LaunchedEffect(workplaceId) { refresh() }

    // Geolocation distance update
    LaunchedEffect(workplaceId, geoSettings) {
        val g = geoSettings
        if (g != null && g.latitude != null && g.longitude != null) {
            val geo = kotlinx.browser.window.navigator.asDynamic().geolocation
            geo?.getCurrentPosition({ pos ->
                val lat = pos.coords.latitude as Double
                val lon = pos.coords.longitude as Double
                currentDistance = clientHaversine(lat, lon, g.latitude!!, g.longitude!!)
            }, { err ->
                geoError = "Kunde inte läsa GPS-position"
            })
        } else {
            currentDistance = null
            geoError = null
        }
    }

    if (loading) {
        Div({ style { property("text-align", "center"); property("padding", "3rem"); property("color", "var(--rp-pine)"); property("font-weight", "700") } }) { Text("Laddar...") }
        return
    }

    fun handleCheckIn() {
        val geo = kotlinx.browser.window.navigator.asDynamic().geolocation
        if (geo != null) {
            checkingIn = true
            geo.getCurrentPosition(
                { pos ->
                    val lat = pos.coords.latitude as Double
                    val lon = pos.coords.longitude as Double
                    staffScope.launch {
                        attendanceRepo.checkIn(CheckInRequest(workplaceId, latitude = lat, longitude = lon)).onSuccess {
                            feedbackMsg = "✓ Incheckad!"
                            activeAttendance = it
                            refresh()
                        }.onFailure {
                            feedbackMsg = "Kunde inte checka in: ${it.message}"
                        }
                        checkingIn = false
                    }
                },
                { err ->
                    // Fallback to coordinates-less check-in
                    staffScope.launch {
                        attendanceRepo.checkIn(CheckInRequest(workplaceId)).onSuccess {
                            feedbackMsg = "✓ Incheckad!"
                            activeAttendance = it
                            refresh()
                        }.onFailure {
                            feedbackMsg = "Kunde inte checka in: ${it.message}"
                        }
                        checkingIn = false
                    }
                }
            )
        } else {
            checkingIn = true
            staffScope.launch {
                attendanceRepo.checkIn(CheckInRequest(workplaceId)).onSuccess {
                    feedbackMsg = "✓ Incheckad!"
                    activeAttendance = it
                    refresh()
                }.onFailure {
                    feedbackMsg = "Kunde inte checka in: ${it.message}"
                }
                checkingIn = false
            }
        }
    }

    Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "1.5rem") } }) {

        // Feedback notification
        feedbackMsg?.let { msg ->
            Div({
                style {
                    property("background", if (msg.startsWith("✓")) "var(--rp-ok-bg)" else "var(--rp-warn-bg)")
                    property("color", if (msg.startsWith("✓")) "var(--rp-ok-text)" else "var(--rp-warn-text)")
                    property("padding", "0.75rem 1.25rem"); property("border-radius", "14px"); property("font-weight", "600")
                }
            }) { Text(msg) }
        }

        // Welcome / Time tracker Card
        Div({
            style {
                property("background", "linear-gradient(135deg, var(--rp-pine), var(--rp-iris))")
                property("border-radius", "24px"); property("padding", "1.75rem"); property("color", "white")
                property("box-shadow", "0 10px 25px rgba(44, 63, 168, 0.15)")
            }
        }) {
            H3({ style { property("margin", "0 0 0.5rem 0"); property("font-size", "1.4rem"); property("font-weight", "800") } }) {
                Text("Hej ${userInfo?.email?.substringBefore("@") ?: "medarbetare"}!")
            }
            P({ style { property("margin", "0 0 1.5rem 0"); property("opacity", "0.9"); property("font-size", "0.95rem") } }) {
                Text("Välkommen till ditt Mittpass. Här ser du dagens status, dina pass och kan checka in på jobbet.")
            }

            // Attendance Section
            Div({
                style {
                    property("background", "rgba(255, 255, 255, 0.12)"); property("border-radius", "16px")
                    property("padding", "1.25rem"); property("display", "flex"); property("flex-direction", "column"); property("gap", "1rem")
                }
            }) {
                if (activeAttendance != null) {
                    val inTime = activeAttendance!!.checkInTime.let {
                        if (it.contains("T")) it.substringAfter("T").take(5) else it.take(5)
                    }
                    Div({ style { property("display", "flex"); property("align-items", "center"); property("justify-content", "space-between"); property("flex-wrap", "wrap"); property("gap", "1rem") } }) {
                        Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem") } }) {
                            FaIcon("fa-solid fa-circle", size = "0.55rem", color = "var(--rp-ok-text)")
                            Span({ style { property("font-weight", "700") } }) { Text("Du är incheckad sedan $inTime") }
                        }
                        Button({
                            onClick {
                                checkingOut = true
                                staffScope.launch {
                                    attendanceRepo.checkOut(CheckOutRequest(activeAttendance!!.id)).onSuccess {
                                        feedbackMsg = "✓ Utcheckad!"
                                        activeAttendance = null
                                    }.onFailure { feedbackMsg = "Kunde inte checka ut: ${it.message}" }
                                    checkingOut = false
                                    refresh()
                                }
                            }
                            if (checkingOut) disabled()
                            style {
                                property("padding", "0.6rem 1.25rem"); property("border", "none")
                                property("background", "var(--rp-love)"); property("color", "white")
                                property("border-radius", "50px"); property("font-weight", "700"); property("font-size", "0.85rem"); property("cursor", "pointer")
                            }
                        }) { Text(if (checkingOut) "Checkar ut..." else "Checka ut") }
                    }
                } else {
                    Div({ style { property("display", "flex"); property("align-items", "center"); property("justify-content", "space-between"); property("flex-wrap", "wrap"); property("gap", "1rem") } }) {
                        Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "0.3rem") } }) {
                            Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem") } }) {
                                FaIcon("fa-regular fa-circle", size = "0.55rem", color = "var(--rp-hl-med)")
                                Span({ style { property("font-weight", "700"); property("opacity", "0.85") } }) { Text("Du är inte incheckad") }
                            }
                            // Geofence status message
                            val geo = geoSettings
                            if (geo != null && geo.latitude != null && geo.longitude != null) {
                                val dist = currentDistance
                                val radius = geo.geofenceRadiusMeters
                                if (dist != null) {
                                    if (dist <= radius) {
                                        Span({ style { property("font-size", "0.8rem"); property("color", "var(--rp-ok-text)"); property("font-weight", "600") } }) {
                                            Text("✓ Inom räckhåll (${dist.toInt()} m från workplace)")
                                        }
                                    } else {
                                        Span({ style { property("font-size", "0.8rem"); property("color", "var(--rp-err-border)"); property("font-weight", "600") } }) {
                                            Text("För långt bort (${dist.toInt()} m, max $radius m)")
                                        }
                                    }
                                } else {
                                    Span({ style { property("font-size", "0.8rem"); property("opacity", "0.7") } }) {
                                        Text("Söker GPS-position...")
                                    }
                                }
                            }
                        }
                        Button({
                            onClick { handleCheckIn() }
                            if (checkingIn) disabled()
                            style {
                                property("padding", "0.6rem 1.25rem"); property("border", "none")
                                property("background", "var(--rp-pine)"); property("color", "white")
                                property("border-radius", "50px"); property("font-weight", "700"); property("font-size", "0.85rem"); property("cursor", "pointer")
                            }
                        }) { Text(if (checkingIn) "Checkar in..." else "Checka in") }
                    }
                }
            }
        }

        // ── Earned Preview Card (Preliminär intjänad bruttolön för perioden) ──
        stats?.let { s ->
            var showEarnedModal by remember { mutableStateOf(false) }

            // Hero Earnings banner for hourly staff (and monthly staff if set)
            if (s.hourlyRateOre != null || s.approvedGrossPaySek > 0.0 || s.pendingGrossPaySek > 0.0) {
                Div({
                    style {
                        property("background", "linear-gradient(135deg, var(--rp-pine), var(--rp-iris))")
                        property("border-radius", "20px"); property("padding", "1.5rem")
                        property("color", "white"); property("box-shadow", "0 10px 25px -5px rgba(49, 46, 129, 0.2)")
                        property("display", "flex"); property("flex-direction", "column"); property("gap", "1rem")
                    }
                }) {
                    Div({
                        style {
                            property("display", "flex"); property("align-items", "center"); property("justify-content", "space-between")
                            property("flex-wrap", "wrap"); property("gap", "0.75rem")
                        }
                    }) {
                        Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.6rem") } }) {
                            Div({
                                style {
                                    property("width", "36px"); property("height", "36px"); property("border-radius", "10px")
                                    property("background", "rgba(255, 255, 255, 0.15)"); property("display", "flex")
                                    property("align-items", "center"); property("justify-content", "center")
                                }
                            }) {
                                FaIcon("fa-solid fa-sack-dollar", size = "1.1rem", color = "var(--rp-ok-text)")
                            }
                            Div {
                                Div({ style { property("font-size", "0.75rem"); property("font-weight", "700"); property("text-transform", "uppercase"); property("letter-spacing", "0.05em"); property("color", "var(--rp-info-border)") } }) {
                                    Text("Intjänat denna löneperiod")
                                }
                                if (s.periodStart.isNotBlank() && s.periodEnd.isNotBlank()) {
                                    Div({ style { property("font-size", "0.8rem"); property("color", "var(--rp-info-bg)"); property("opacity", "0.85") } }) {
                                        Text("${s.periodStart} – ${s.periodEnd}")
                                    }
                                }
                            }
                        }

                        // Next payday badge
                        if (s.nextPayday.isNotBlank()) {
                            Div({
                                style {
                                    property("background", "rgba(255, 255, 255, 0.1)"); property("border", "1px solid rgba(255, 255, 255, 0.2)")
                                    property("border-radius", "9999px"); property("padding", "0.35rem 0.85rem")
                                    property("font-size", "0.78rem"); property("font-weight", "700"); property("display", "flex")
                                    property("align-items", "center"); property("gap", "0.4rem")
                                }
                            }) {
                                FaIcon("fa-solid fa-calendar-check", size = "0.75rem", color = "var(--rp-warn-border)")
                                Text("Utbetalning: ${s.nextPayday}")
                            }
                        }
                    }

                    Div({
                        style {
                            property("display", "flex"); property("align-items", "baseline"); property("justify-content", "space-between")
                            property("flex-wrap", "wrap"); property("gap", "0.5rem")
                        }
                    }) {
                        Div {
                            Div({ style { property("font-size", "2.25rem"); property("font-weight", "900"); property("color", "var(--rp-ok-text)"); property("letter-spacing", "-0.02em") } }) {
                                Text("${s.approvedGrossPaySek.toInt()} kr")
                            }
                            Div({ style { property("font-size", "0.8rem"); property("color", "var(--rp-info-border)"); property("margin-top", "0.2rem") } }) {
                                Text("Preliminär bruttolön för godkända pass (inkl. OB & 12% semesterersättning)")
                            }
                        }

                        Button({
                            onClick { showEarnedModal = true }
                            style {
                                property("padding", "0.5rem 1rem"); property("border", "1px solid rgba(255, 255, 255, 0.3)")
                                property("background", "rgba(255, 255, 255, 0.1)"); property("color", "white")
                                property("border-radius", "10px"); property("font-weight", "700"); property("font-size", "0.8rem")
                                property("cursor", "pointer"); property("display", "flex"); property("align-items", "center"); property("gap", "0.4rem")
                            }
                        }) {
                            FaIcon("fa-solid fa-chart-pie", size = "0.8rem", color = "var(--rp-info-border)")
                            Text("Specifikation")
                        }
                    }

                    if (s.pendingGrossPaySek > 0.0) {
                        Div({
                            style {
                                property("background", "rgba(245, 158, 11, 0.15)"); property("border", "1px solid rgba(245, 158, 11, 0.3)")
                                property("border-radius", "8px"); property("padding", "0.5rem 0.75rem")
                                property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem")
                                property("font-size", "0.78rem"); property("color", "var(--rp-warn-border)")
                            }
                        }) {
                            FaIcon("fa-solid fa-hourglass-half", size = "0.75rem", color = "var(--rp-warn-text)")
                            Text("+ ${s.pendingGrossPaySek.toInt()} kr väntar på godkännande av chef")
                        }
                    }
                }
            }

            // Specification Modal
            if (showEarnedModal) {
                Div({
                    style {
                        property("position", "fixed"); property("inset", "0"); property("background", "rgba(15, 23, 42, 0.65)")
                        property("z-index", "1000"); property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                        property("padding", "1rem"); property("backdrop-filter", "blur(4px)")
                    }
                    onClick { showEarnedModal = false }
                }) {
                    Div({
                        style {
                            property("background", "var(--rp-surface)"); property("border-radius", "20px"); property("padding", "1.75rem")
                            property("max-width", "480px"); property("width", "100%"); property("box-shadow", "0 20px 25px -5px rgba(0, 0, 0, 0.15)")
                            property("display", "flex"); property("flex-direction", "column"); property("gap", "1.25rem")
                        }
                        onClick { it.stopPropagation() }
                    }) {
                        Div({ style { property("display", "flex"); property("align-items", "center"); property("justify-content", "space-between") } }) {
                            Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem") } }) {
                                FaIcon("fa-solid fa-calculator", size = "1.1rem", color = "var(--rp-pine)")
                                H3({ style { property("margin", "0"); property("font-size", "1.15rem"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) {
                                    Text("Lönespecifikation (Preliminär)")
                                }
                            }
                            Button({
                                onClick { showEarnedModal = false }
                                style { property("background", "transparent"); property("border", "none"); property("cursor", "pointer"); property("color", "var(--rp-subtle)") }
                            }) { FaIcon("fa-solid fa-xmark", size = "1.1rem", color = "var(--rp-subtle)") }
                        }

                        P({ style { property("margin", "0"); property("font-size", "0.82rem"); property("color", "var(--rp-subtle)"); property("line-height", "1.4") } }) {
                            Text("Här ser du en preliminär beräkning av din bruttolön baserad på dina godkända arbetspass under perioden ${s.periodStart} – ${s.periodEnd}.")
                        }

                        Div({
                            style {
                                property("background", "var(--rp-overlay)"); property("border-radius", "12px"); property("padding", "1rem")
                                property("display", "flex"); property("flex-direction", "column"); property("gap", "0.75rem")
                            }
                        }) {
                            if (s.approvedBasePaySek > 0 || s.approvedMinutes > 0) {
                                Div({ style { property("display", "flex"); property("justify-content", "space-between"); property("font-size", "0.88rem") } }) {
                                    Span({ style { property("color", "var(--rp-subtle)") } }) {
                                        Text("Grundlön (${formatMinutes(s.approvedMinutes.toLong())}${s.hourlyRateOre?.let { " á ${(it / 100.0).toInt()} kr/h" } ?: ""})")
                                    }
                                    Span({ style { property("font-weight", "700"); property("color", "var(--rp-text)") } }) {
                                        Text("${s.approvedBasePaySek.toInt()} kr")
                                    }
                                }
                            }
                            if (s.approvedObPaySek > 0) {
                                Div({ style { property("display", "flex"); property("justify-content", "space-between"); property("font-size", "0.88rem") } }) {
                                    Span({ style { property("color", "var(--rp-subtle)") } }) { Text("OB-ersättning (kväll, natt, helg)") }
                                    Span({ style { property("font-weight", "700"); property("color", "var(--rp-text)") } }) {
                                        Text("${s.approvedObPaySek.toInt()} kr")
                                    }
                                }
                            }
                            if (s.approvedSickPaySek > 0 || s.approvedSickMinutes > 0) {
                                Div({ style { property("display", "flex"); property("justify-content", "space-between"); property("font-size", "0.88rem") } }) {
                                    Span({ style { property("color", "var(--rp-subtle)") } }) {
                                        Text("Sjuklön (80% efter karensdag, ${formatMinutes(s.approvedSickMinutes.toLong())})")
                                    }
                                    Span({ style { property("font-weight", "700"); property("color", "var(--rp-text)") } }) {
                                        Text("${s.approvedSickPaySek.toInt()} kr")
                                    }
                                }
                            }
                            if (s.approvedVacationPaySek > 0) {
                                Div({ style { property("display", "flex"); property("justify-content", "space-between"); property("font-size", "0.88rem") } }) {
                                    Span({ style { property("color", "var(--rp-subtle)") } }) { Text("Semesterersättning (12%)") }
                                    Span({ style { property("font-weight", "700"); property("color", "var(--rp-text)") } }) {
                                        Text("${s.approvedVacationPaySek.toInt()} kr")
                                    }
                                }
                            }
                            Div({ style { property("height", "1px"); property("background", "var(--rp-overlay)") } }) {}
                            Div({ style { property("display", "flex"); property("justify-content", "space-between"); property("font-size", "0.95rem"); property("font-weight", "800") } }) {
                                Span({ style { property("color", "var(--rp-text)") } }) { Text("Bruttolön") }
                                Span({ style { property("color", "var(--rp-text)") } }) { Text("${s.approvedGrossPaySek.toInt()} kr") }
                            }
                            if (s.approvedTaxDeductionSek > 0) {
                                val taxLbl = when {
                                    !s.taxTable.isNullOrBlank() -> "Preliminärskatt (tabell ${s.taxTable})"
                                    s.taxRatePct != null -> "Preliminärskatt (${s.taxRatePct}%)"
                                    else -> "Preliminärskatt (30%)"
                                }
                                Div({ style { property("display", "flex"); property("justify-content", "space-between"); property("font-size", "0.88rem") } }) {
                                    Span({ style { property("color", "var(--rp-err-text)") } }) { Text(taxLbl) }
                                    Span({ style { property("font-weight", "700"); property("color", "var(--rp-err-text)") } }) {
                                        Text("-${s.approvedTaxDeductionSek.toInt()} kr")
                                    }
                                }
                            }
                            Div({ style { property("height", "1px"); property("background", "var(--rp-hl-med)") } }) {}
                            Div({ style { property("display", "flex"); property("justify-content", "space-between"); property("font-size", "1.1rem"); property("font-weight", "900") } }) {
                                Span({ style { property("color", "var(--rp-ok-text)") } }) { Text("Uppskattat netto (utbetalas)") }
                                Span({ style { property("color", "var(--rp-ok-text)") } }) { Text("${s.approvedNetPaySek.toInt()} kr") }
                            }
                        }

                        // Arbetsgivaravgift & personalkostnad (sociala avgifter)
                        Div({
                            style {
                                property("background", "var(--rp-overlay)"); property("border", "1px solid var(--rp-overlay)")
                                property("border-radius", "12px"); property("padding", "0.85rem 1rem")
                                property("display", "flex"); property("flex-direction", "column"); property("gap", "0.35rem")
                                property("font-size", "0.8rem"); property("color", "var(--rp-subtle)")
                            }
                        }) {
                            Div({ style { property("font-weight", "700"); property("color", "var(--rp-text)"); property("margin-bottom", "0.15rem") } }) {
                                Text("Företagets kostnader (Sociala avgifter)")
                            }
                            Div({ style { property("display", "flex"); property("justify-content", "space-between") } }) {
                                Span { Text("Arbetsgivaravgift (31,42%):") }
                                Span({ style { property("font-weight", "700") } }) { Text("${s.approvedEmployerContributionSek.toInt()} kr") }
                            }
                            Div({ style { property("display", "flex"); property("justify-content", "space-between") } }) {
                                Span { Text("Total preliminär personalkostnad:") }
                                Span({ style { property("font-weight", "800"); property("color", "var(--rp-text)") } }) { Text("${s.approvedTotalCostSek.toInt()} kr") }
                            }
                        }

                        // Semestersaldo
                        Div({
                            style {
                                property("background", "var(--rp-ok-bg)"); property("border", "1px solid var(--rp-ok-border)")
                                property("border-radius", "12px"); property("padding", "0.85rem 1rem")
                                property("display", "grid"); property("grid-template-columns", "repeat(3, 1fr)")
                                property("gap", "0.5rem"); property("text-align", "center")
                            }
                        }) {
                            Div {
                                Div({ style { property("font-size", "0.72rem"); property("color", "var(--rp-ok-text)"); property("font-weight", "600") } }) { Text("Semesterrätt") }
                                Div({ style { property("font-size", "1.05rem"); property("font-weight", "800"); property("color", "var(--rp-ok-text)"); property("margin-top", "0.15rem") } }) { Text("${s.vacationDaysTotal} dgr") }
                            }
                            Div {
                                Div({ style { property("font-size", "0.72rem"); property("color", "var(--rp-ok-text)"); property("font-weight", "600") } }) { Text("Uttagna i år") }
                                val usedDgr = if (s.approvedVacationDaysUsed % 1.0 == 0.0) s.approvedVacationDaysUsed.toInt().toString() else s.approvedVacationDaysUsed.toString()
                                Div({ style { property("font-size", "1.05rem"); property("font-weight", "800"); property("color", "var(--rp-ok-text)"); property("margin-top", "0.15rem") } }) { Text("$usedDgr dgr") }
                            }
                            Div {
                                Div({ style { property("font-size", "0.72rem"); property("color", "var(--rp-ok-text)"); property("font-weight", "600") } }) { Text("Kvarvarande") }
                                val remDgr = if (s.vacationDaysRemaining % 1.0 == 0.0) s.vacationDaysRemaining.toInt().toString() else s.vacationDaysRemaining.toString()
                                Div({ style { property("font-size", "1.05rem"); property("font-weight", "800"); property("color", "var(--rp-ok-text)"); property("margin-top", "0.15rem") } }) { Text("$remDgr dgr") }
                            }
                        }

                        if (s.nextPayday.isNotBlank()) {
                            Div({ style { property("font-size", "0.8rem"); property("color", "var(--rp-subtle)"); property("text-align", "center") } }) {
                                Text("Preliminärt utbetalningsdatum: ")
                                Span({ style { property("font-weight", "700"); property("color", "var(--rp-text)") } }) { Text(s.nextPayday) }
                            }
                        }

                        Button({
                            onClick { showEarnedModal = false }
                            style {
                                property("width", "100%"); property("padding", "0.75rem"); property("border", "none")
                                property("background", "var(--rp-pine)"); property("color", "white"); property("border-radius", "12px")
                                property("font-weight", "700"); property("cursor", "pointer")
                            }
                        }) { Text("Stäng") }
                    }
                }
            }

            // Stats grid
            Div({ style { property("display", "grid"); property("grid-template-columns", "repeat(auto-fit, minmax(130px, 1fr))"); property("gap", "0.85rem") } }) {
                StatCard("fa-regular fa-clock", "Arbetad tid", formatMinutes(s.totalWorkedMinutes), "var(--rp-info-bg)", "var(--rp-pine)")
                StatCard("fa-solid fa-coins", "Godkänd tid", formatMinutes(s.totalPaidMinutes), "var(--rp-ok-bg)", "var(--rp-ok-text)")
                StatCard("fa-regular fa-hourglass", "Preliminärt", formatMinutes(s.preliminaryMinutes), "var(--rp-warn-bg)", "var(--rp-warn-text)")
                StatCard("fa-solid fa-calendar-days", "Kommande pass", "${s.upcomingSlots} st", "var(--rp-info-bg)", "var(--rp-pine)")
            }
        }

        // HACCP, Messages & Payslips Quick Widgets row
        Div({ style { property("display", "grid"); property("grid-template-columns", "repeat(auto-fit, minmax(260px, 1fr))"); property("gap", "1rem") } }) {
            
            // Payslips Widget (Super clear & intuitive for staff of all ages)
            Div({
                style {
                    property("background", "var(--rp-surface)"); property("border-radius", "20px"); property("padding", "1.25rem")
                    property("border", "1px solid var(--rp-hl-med)"); property("display", "flex"); property("flex-direction", "column"); property("justify-content", "space-between")
                }
            }) {
                Div {
                    Div({ style { property("display", "flex"); property("align-items", "center"); property("justify-content", "space-between"); property("margin-bottom", "0.75rem") } }) {
                        H4({ style { property("margin", "0"); property("font-size", "1rem"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) {
                            Text("Mina lönebesked")
                        }
                        FaIcon("fa-solid fa-file-invoice-dollar", size = "1.1rem", color = "var(--rp-ok-text)")
                    }
                    P({ style { property("font-size", "0.85rem"); property("color", "var(--rp-subtle)"); property("margin", "0 0 1rem 0"); property("line-height", "1.4") } }) {
                        Text("Se dina utbetalningar, OB-tillägg och ladda ner färdiga lönebesked som PDF.")
                    }
                }
                Button({
                    onClick { onNavigate("LONEBESKED") }
                    style {
                        property("margin-top", "1rem"); property("width", "100%"); property("padding", "0.6rem")
                        property("background", "linear-gradient(135deg, var(--rp-pine), var(--rp-foam))"); property("border", "none"); property("border-radius", "10px")
                        property("font-weight", "700"); property("font-size", "0.85rem"); property("color", "white"); property("cursor", "pointer")
                        property("display", "flex"); property("align-items", "center"); property("justify-content", "center"); property("gap", "0.4rem")
                    }
                }) {
                    FaIcon("fa-solid fa-file-pdf", size = "0.85rem", color = "white")
                    Text("Öppna lönebesked")
                }
            }

            // HACCP widget
            Div({
                style {
                    property("background", "var(--rp-surface)"); property("border-radius", "20px"); property("padding", "1.25rem")
                    property("border", "1px solid var(--rp-hl-med)"); property("display", "flex"); property("flex-direction", "column"); property("justify-content", "space-between")
                }
            }) {
                Div {
                    Div({ style { property("display", "flex"); property("align-items", "center"); property("justify-content", "space-between"); property("margin-bottom", "0.75rem") } }) {
                        H4({ style { property("margin", "0"); property("font-size", "1rem"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) {
                            Text("Egenkontroll (HACCP)")
                        }
                        FaIcon("fa-solid fa-flask", size = "1rem", color = "var(--rp-pine)")
                    }
                    P({ style { property("font-size", "0.85rem"); property("color", "var(--rp-subtle)"); property("margin", "0 0 1rem 0") } }) {
                        Text("Se till att alla temperaturer och städrutiner är dokumenterade för idag.")
                    }
                    Div({ style { property("background", "var(--rp-overlay)"); property("height", "8px"); property("border-radius", "4px"); property("overflow", "hidden"); property("margin-bottom", "0.5rem") } }) {
                        val pct = if (haccpExpected > 0) (haccpCompleted.toDouble() / haccpExpected * 100).toInt() else 0
                        Div({ style { property("background", "var(--rp-pine)"); property("height", "100%"); property("width", "$pct%") } })
                    }
                    Span({ style { property("font-size", "0.8rem"); property("font-weight", "700"); property("color", "var(--rp-subtle)") } }) {
                        Text("$haccpCompleted av $haccpExpected kontroller utförda idag")
                    }
                }
                Button({
                    onClick { onNavigate("EGENKONTROLL") }
                    style {
                        property("margin-top", "1rem"); property("width", "100%"); property("padding", "0.5rem")
                        property("background", "var(--rp-overlay)"); property("border", "none"); property("border-radius", "10px")
                        property("font-weight", "700"); property("font-size", "0.8rem"); property("color", "var(--rp-pine)"); property("cursor", "pointer")
                    }
                }) { Text("Gå till Egenkontroll") }
            }

            // Messages/Chat widget
            Div({
                style {
                    property("background", "var(--rp-surface)"); property("border-radius", "20px"); property("padding", "1.25rem")
                    property("border", "1px solid var(--rp-hl-med)"); property("display", "flex"); property("flex-direction", "column"); property("justify-content", "space-between")
                }
            }) {
                Div {
                    Div({ style { property("display", "flex"); property("align-items", "center"); property("justify-content", "space-between"); property("margin-bottom", "0.75rem") } }) {
                        H4({ style { property("margin", "0"); property("font-size", "1rem"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) {
                            Text("Gruppmeddelanden")
                        }
                        FaIcon("fa-solid fa-comments", size = "1rem", color = "var(--rp-pine)")
                    }
                    if (recentMessages.isEmpty()) {
                        P({ style { property("font-size", "0.85rem"); property("color", "var(--rp-muted)"); property("font-style", "italic"); property("margin", "1rem 0") } }) {
                            Text("Inga nya meddelanden i kanalen.")
                        }
                    } else {
                        recentMessages.forEach { msg ->
                            Div({ style { property("border-bottom", "1px solid var(--rp-hl-med)"); property("padding", "0.4rem 0") } }) {
                                Span({ style { property("font-size", "0.78rem"); property("font-weight", "700"); property("color", "var(--rp-pine)") } }) {
                                    Text("${msg.senderName}: ")
                                }
                                Span({ style { property("font-size", "0.78rem"); property("color", "var(--rp-subtle)") } }) {
                                    Text(msg.content)
                                }
                            }
                        }
                    }
                }
                Button({
                    onClick { onNavigate("MEDDELANDEN") }
                    style {
                        property("margin-top", "1rem"); property("width", "100%"); property("padding", "0.5rem")
                        property("background", "var(--rp-overlay)"); property("border", "none"); property("border-radius", "10px")
                        property("font-weight", "700"); property("font-size", "0.8rem"); property("color", "var(--rp-pine)"); property("cursor", "pointer")
                    }
                }) { Text("Öppna Meddelanden") }
            }
        }

        // Upcoming shifts (Mina kommande pass)
        Div({ style { property("background", "var(--rp-surface)"); property("border-radius", "24px"); property("padding", "1.5rem"); property("border", "1px solid var(--rp-overlay)") } }) {
            H3({ style { property("margin", "0 0 1.25rem 0"); property("font-size", "1.2rem"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) {
                Text("Mina kommande pass")
            }

            if (myClaims.isEmpty()) {
                Div({ style { property("text-align", "center"); property("padding", "2rem"); property("color", "var(--rp-muted)") } }) {
                    Text("Du har inga bokade pass.")
                }
            } else {
                myClaims.sortedBy { it.day + it.startTime }.forEach { claim ->
                    val canExit = canExitSlot(claim.day, claim.startTime)
                    Div({
                        style {
                            property("display", "flex"); property("align-items", "center"); property("justify-content", "space-between")
                            property("padding", "1rem"); property("border-bottom", "1px solid var(--rp-hl-med)")
                        }
                    }) {
                        Div {
                            Div({ style { property("font-weight", "700"); property("color", "var(--rp-text)") } }) {
                                Text("${claim.day} \u2022 ${claim.startTime} \u2013 ${claim.endTime}")
                            }
                            Div({ style { property("font-size", "0.82rem"); property("color", "var(--rp-subtle)"); property("margin-top", "0.15rem") } }) {
                                Text(claim.workplaceName)
                            }
                        }
                        if (canExit) {
                            Button({
                                onClick { exitConfirmClaim = claim }
                                style {
                                    property("padding", "0.45rem 0.95rem"); property("border", "1.5px solid var(--rp-err-border)")
                                    property("background", "transparent"); property("color", "var(--rp-love)")
                                    property("border-radius", "50px"); property("font-weight", "700"); property("font-size", "0.8rem"); property("cursor", "pointer")
                                }
                            }) { Text("Lämna pass") }
                        } else {
                            Span({ style { property("font-size", "0.8rem"); property("color", "var(--rp-muted)"); property("font-style", "italic") } }) {
                                Text("< 24h kvar")
                            }
                        }
                    }
                }
            }
        }

        // Available Shifts (Lediga pass)
        Div({ style { property("background", "var(--rp-surface)"); property("border-radius", "24px"); property("padding", "1.5rem"); property("border", "1px solid var(--rp-overlay)") } }) {
            H3({ style { property("margin", "0 0 1.25rem 0"); property("font-size", "1.2rem"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) {
                Text("Lediga pass att söka")
            }

            // Exclude slots user has already claimed
            val claimable = availableSlots.filter { slot ->
                myClaims.none { it.slotId == slot.id }
            }.sortedBy { it.day + it.startTime }

            if (claimable.isEmpty()) {
                Div({ style { property("text-align", "center"); property("padding", "2rem"); property("color", "var(--rp-muted)") } }) {
                    Text("Det finns inga lediga pass just nu.")
                }
            } else {
                claimable.forEach { slot ->
                    Div({
                        style {
                            property("display", "flex"); property("align-items", "center"); property("justify-content", "space-between")
                            property("padding", "1rem"); property("border-bottom", "1px solid var(--rp-hl-med)")
                        }
                    }) {
                        Div {
                            Div({ style { property("font-weight", "700"); property("color", "var(--rp-text)") } }) {
                                Text("${slot.day} \u2022 ${slot.startTime} \u2013 ${slot.endTime}")
                            }
                            Div({ style { property("font-size", "0.82rem"); property("color", "var(--rp-ok-text)"); property("font-weight", "600"); property("margin-top", "0.15rem") } }) {
                                Text("Behöver täckning")
                            }
                        }
                        Button({
                            onClick { claimConfirmSlot = slot }
                            style {
                                property("padding", "0.45rem 1rem"); property("border", "none")
                                property("background", "linear-gradient(135deg, var(--rp-pine), var(--rp-foam))"); property("color", "white")
                                property("border-radius", "50px"); property("font-weight", "700"); property("font-size", "0.8rem"); property("cursor", "pointer")
                            }
                        }) { Text("Ta pass") }
                    }
                }
            }
        }
    }

    // Exit confirmation modal
    exitConfirmClaim?.let { claim ->
        Div({
            onClick { exitConfirmClaim = null }
            style {
                property("position", "fixed"); property("inset", "0"); property("z-index", "1000")
                property("background", "rgba(0,0,0,0.45)"); property("display", "flex")
                property("align-items", "center"); property("justify-content", "center")
            }
        }) {
            Div({
                onClick { it.stopPropagation() }
                style {
                    property("background", "var(--rp-surface)"); property("border-radius", "24px")
                    property("padding", "2rem"); property("width", "min(420px, 90vw)"); property("text-align", "center")
                }
            }) {
                Div({ style { property("display", "flex"); property("justify-content", "center"); property("margin-bottom", "0.75rem") } }) {
                    FaIcon("fa-solid fa-triangle-exclamation", size = "2.5rem", color = "var(--rp-warn-text)")
                }
                H3({ style { property("margin", "0 0 0.5rem 0"); property("font-size", "1.2rem"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) {
                    Text("Lämna pass?")
                }
                P({ style { property("color", "var(--rp-subtle)"); property("margin", "0 0 1.5rem 0") } }) {
                    Text("${claim.day} ${claim.startTime}\u2013${claim.endTime}")
                    Br()
                    Text("Chef meddelas automatiskt.")
                }
                Div({ style { property("display", "flex"); property("gap", "0.75rem"); property("justify-content", "center") } }) {
                    Button({
                        onClick { exitConfirmClaim = null }
                        style { property("padding", "0.75rem 1.5rem"); property("border", "2px solid var(--rp-pine)"); property("background", "transparent"); property("color", "var(--rp-pine)"); property("border-radius", "50px"); property("font-weight", "700"); property("cursor", "pointer") }
                    }) { Text("Avbryt") }
                    Button({
                        onClick {
                            staffScope.launch {
                                staffRepo.exitSlot(claim.id).onSuccess {
                                    feedbackMsg = "✓ Du har lämnat passet. Chef meddelas."
                                    exitConfirmClaim = null
                                    refresh()
                                }.onFailure {
                                    feedbackMsg = "Kunde inte lämna: ${it.message}"
                                    exitConfirmClaim = null
                                }
                            }
                        }
                        style { property("padding", "0.75rem 1.5rem"); property("border", "none"); property("background", "linear-gradient(135deg, var(--rp-love), var(--rp-love))"); property("color", "white"); property("border-radius", "50px"); property("font-weight", "700"); property("cursor", "pointer") }
                    }) { Text("Lämna") }
                }
            }
        }
    }

    // Claim confirmation modal
    claimConfirmSlot?.let { slot ->
        Div({
            onClick { claimConfirmSlot = null }
            style {
                property("position", "fixed"); property("inset", "0"); property("z-index", "1000")
                property("background", "rgba(0,0,0,0.45)"); property("display", "flex")
                property("align-items", "center"); property("justify-content", "center")
            }
        }) {
            Div({
                onClick { it.stopPropagation() }
                style {
                    property("background", "var(--rp-surface)"); property("border-radius", "24px")
                    property("padding", "2rem"); property("width", "min(420px, 90vw)"); property("text-align", "center")
                }
            }) {
                Div({ style { property("display", "flex"); property("justify-content", "center"); property("margin-bottom", "0.75rem") } }) {
                    FaIcon("fa-solid fa-circle-question", size = "2.5rem", color = "var(--rp-ok-text)")
                }
                H3({ style { property("margin", "0 0 0.5rem 0"); property("font-size", "1.2rem"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) {
                    Text("Ta passet?")
                }
                P({ style { property("color", "var(--rp-subtle)"); property("margin", "0 0 1.5rem 0") } }) {
                    Text("${slot.day} ${slot.startTime}\u2013${slot.endTime}")
                    Br()
                    Text("Detta pass läggs till i ditt schema direkt.")
                }
                Div({ style { property("display", "flex"); property("gap", "0.75rem"); property("justify-content", "center") } }) {
                    Button({
                        onClick { claimConfirmSlot = null }
                        style { property("padding", "0.75rem 1.5rem"); property("border", "2px solid var(--rp-hl-med)"); property("background", "transparent"); property("color", "var(--rp-subtle)"); property("border-radius", "50px"); property("font-weight", "700"); property("cursor", "pointer") }
                    }) { Text("Avbryt") }
                    Button({
                        onClick {
                            staffScope.launch {
                                claimsRepo.claimSlot(slot.id, assign = true).onSuccess {
                                    feedbackMsg = "✓ Passet bokat!"
                                    claimConfirmSlot = null
                                    refresh()
                                }.onFailure {
                                    feedbackMsg = "Kunde inte ta passet: ${it.message}"
                                    claimConfirmSlot = null
                                }
                            }
                        }
                        style { property("padding", "0.75rem 1.5rem"); property("border", "none"); property("background", "linear-gradient(135deg, var(--rp-pine), var(--rp-foam))"); property("color", "white"); property("border-radius", "50px"); property("font-weight", "700"); property("cursor", "pointer") }
                    }) { Text("Boka pass") }
                }
            }
        }
    }
}

@Composable
private fun StatCard(iconClasses: String, label: String, value: String, bgColor: String, textColor: String) {
    Div({
        style {
            property("background", bgColor); property("border-radius", "18px"); property("padding", "1.25rem")
            property("text-align", "center"); property("border", "1px solid rgba(0,0,0,0.03)")
        }
    }) {
        Div({ style { property("display", "flex"); property("justify-content", "center"); property("margin-bottom", "0.35rem") } }) {
            FaIcon(iconClasses, size = "1.25rem", color = textColor)
        }
        Div({ style { property("font-size", "1.4rem"); property("font-weight", "800"); property("color", textColor) } }) { Text(value) }
        Div({ style { property("font-size", "0.75rem"); property("font-weight", "700"); property("color", textColor); property("margin-top", "0.3rem"); property("opacity", "0.85") } }) { Text(label) }
    }
}

private fun formatMinutes(totalMinutes: Long): String {
    val h = totalMinutes / 60
    val m = totalMinutes % 60
    return if (m > 0) "${h}h ${m}m" else "${h}h"
}

private fun canExitSlot(day: String, startTime: String): Boolean {
    val slotStart = kotlin.js.Date("${day}T${startTime}:00")
    val now = kotlin.js.Date()
    val diffMs = slotStart.getTime() - now.getTime()
    return diffMs > 24 * 60 * 60 * 1000
}

private fun clientHaversine(lat1: Double, lon1: Double, lat2: Double, lon2: Double): Double {
    val r = 6371000.0
    val dLat = (lat2 - lat1) * PI / 180.0
    val dLon = (lon2 - lon1) * PI / 180.0
    val a = sin(dLat / 2.0) * sin(dLat / 2.0) +
            cos(lat1 * PI / 180.0) * cos(lat2 * PI / 180.0) *
            sin(dLon / 2.0) * sin(dLon / 2.0)
    return r * 2.0 * asin(sqrt(a))
}
